<template>
	<Row ref="container">
		<Container>
			<!-- Error state: completely outside .glide so Glide.js never initialises -->
			<div
				v-if="loaded && (!posts || posts.length === 0)"
				class="h-[300px] lg:h-[512px] rounded-xl bg-slate-300/[0.1] flex flex-col items-center justify-center gap-4"
			>
				<h2 class="title lg:text-center text-white text-[24px] lg:text-[36px]">
					Featured Articles
				</h2>
				<p class="text-sm text-white/50 text-center px-6">
					Data is not available at the moment, try again later.
				</p>
			</div>

			<!-- Slider: only rendered when we have posts (or still loading) so Glide always has a valid DOM -->
		<div
			v-else
			ref="glideRoot"
			class="items-center gap-[32px] glide"
		>
				<div class="flex justify-between mb-[32px]">
					<h2
						class="title lg:text-center text-white text-[24px] lg:text-[36px] mb-[16px]"
					>
						Featured Articles
					</h2>

					<div
						class="flex gap-[32px] relative z-[1]"
						data-glide-el="controls"
						v-show="breakpoints.isGreater('small')"
					>
						<button
							class="slider-button"
							data-glide-dir="<"
						>
							<svg width="19" height="16" viewBox="0 0 19 16" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M8 0.929688L9.5 2.42969L4.92969 7H19V9H4.92969L9.5 13.5703L8 15.0703L0.929688 8L8 0.929688Z" fill="white" />
							</svg>
						</button>
						<button
							class="slider-button"
							data-glide-dir=">"
						>
							<svg width="19" height="16" viewBox="0 0 19 16" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M11 0.929688L9.5 2.42969L14.0703 7H0V9H14.0703L9.5 13.5703L11 15.0703L18.0703 8L11 0.929688Z" fill="white" />
							</svg>
						</button>
					</div>
				</div>

				<div class="relative">
					<div class="slider-container">
						<div class="glide__track" data-glide-el="track">
							<ul class="glide__slides" ref="slider">
								<li
									class="glide__slide"
									:key="index"
									v-for="(post, index) in posts"
								>
									<BlogCard
										:key="index"
										v-bind="post"
										:category="
											post.categories && post.categories.length > 0
												? post.categories[0]
												: null
										"
									/>
								</li>
							</ul>
						</div>
					</div>
				</div>

			<div
				class="mt-[32px] flex gap-[32px] justify-center relative z-[1]"
				data-glide-el="controls"
				v-show="!breakpoints.isGreater('small')"
			>
					<button class="slider-button" data-glide-dir="<">
						<svg width="19" height="16" viewBox="0 0 19 16" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M8 0.929688L9.5 2.42969L4.92969 7H19V9H4.92969L9.5 13.5703L8 15.0703L0.929688 8L8 0.929688Z" fill="white" />
						</svg>
					</button>
					<button class="slider-button" data-glide-dir=">">
						<svg width="19" height="16" viewBox="0 0 19 16" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M11 0.929688L9.5 2.42969L14.0703 7H0V9H14.0703L9.5 13.5703L11 15.0703L18.0703 8L11 0.929688Z" fill="white" />
						</svg>
					</button>
				</div>
			</div>
		</Container>
	</Row>
</template>

<script setup>
import Glide from "@glidejs/glide";
import "@glidejs/glide/dist/css/glide.core.min.css";

const container = ref();
const glideRoot = ref(null);
const { posts, categories, loaded = true } = defineProps(["posts", "categories", "loaded"]);
const breakpoints = reactive(
	useBreakpoints({
		small: 768
	})
);

const filterCategory = (id) => {
	const category = categories.find((category) => category.id === id);

	return category;
};

let glideInstance = null;

function initGlide() {
	if (!import.meta.client || !posts || posts.length === 0 || !glideRoot.value) return;

	if (glideInstance) {
		glideInstance.destroy();
	}

	let options = {
		rewind: false,
		bound: true,
		startAt: 0,
		focusAt: 0
	};

	if (breakpoints.isGreater("small")) {
		options = {
			...options,
			perView: 3,
			gap: 24
		};
	} else {
		options = {
			...options,
			perView: 1,
			gap: 24
		};
	}

	glideInstance = new Glide(glideRoot.value, options);
	glideInstance.mount();
}

onMounted(() => {
	nextTick(() => initGlide());
});

watch(
	() => posts,
	(newPosts) => {
		if (newPosts && newPosts.length > 0) {
			nextTick(() => initGlide());
		}
	}
);

onBeforeUnmount(() => {
	if (glideInstance) {
		glideInstance.destroy();
		glideInstance = null;
	}
});
</script>

<style scoped lang="scss">
@use "~/assets/utils.scss" as *;
.glide {
	position: relative;
	z-index: 9;
}

.glide__slide {
	height: auto;
}

.slider-card {
	display: flex;
	flex-direction: column;
	justify-content: flex-end;
	gap: 24px;
	position: relative;
	z-index: 2;
	@include breakpoint(lg) {
		height: 100%;
		min-height: 496px;
	}
	&::before {
		@include breakpoint(lg) {
			content: "";
			position: absolute;
			height: 100%;
			width: 106%;
			left: -16px;
			filter: blur(8px);
			background: linear-gradient(
				178deg,
				rgba(9, 13, 24, 0) 5%,
				rgba($black, 1) 82%
			);
			z-index: 0;
		}
	}
}

.text {
	font-size: 20px;
	color: $white;
	line-height: 1.3;
	letter-spacing: 0.5px;

	@include breakpoint(lg) {
		font-size: 28px;
	}
}

.slider-button {
	height: 48px;
	width: 48px;
	border: 1px solid $blue-300;
	background-color: $blue-300;
	display: flex;
	justify-content: center;
	align-items: center;
	border-radius: 32px;
}

.slider-card-image {
	position: absolute;
	z-index: -1;
	bottom: 64px;
}

.glide__track {
	overflow: hidden;
}

.slider-container {
	top: 0;
	left: 0;
	right: 0;
	margin: auto;
}
#slider {
	position: relative;
	@include breakpoint(lg) {
		left: -172px;
	}
	&::before {
		@include breakpoint(lg) {
			content: "";
			position: absolute;
			top: 0;
			left: 0;
			z-index: 1;
			height: 100%;
			width: 200px;
			background: linear-gradient(
				90deg,
				rgba(9, 13, 24, 1) 20%,
				rgba(0, 0, 0, 0) 95%
			);
		}
	}
}
</style>
